import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, Crown, Clock, X } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { StatusBadge } from "@/components/common/StatusBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_TONE = { owner: "brand", admin: "info", contributor: "neutral" };
const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; } };

export default function TeamRoles({ kind, orgId, isOwner = true, reload }) {
  const [state, setState] = useState(null); // {owner_id, my_role, members, invitations}
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("contributor");
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [transferTo, setTransferTo] = useState("");
  const [confirmTransfer, setConfirmTransfer] = useState(false);
  const [savingPermission, setSavingPermission] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/orgs/${kind}/${orgId}/team`); setState(data); }
    catch { setState({ members: [], invitations: [] }); }
  }, [kind, orgId]);

  useEffect(() => { load(); }, [load]);

  const apply = (data) => setState((s) => ({ ...s, owner_id: data.owner_id ?? s.owner_id, members: data.members }));

  const addMember = async () => {
    if (!email.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/orgs/${kind}/${orgId}/team`, { email: email.trim(), role });
      setEmail("");
      setRole("contributor");
      toast.success(data?.message || "If this person can be invited, we'll send them an invitation.");
      await load(); // refresh pending invitations (members don't change until accepted)
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the invitation.");
    } finally { setAdding(false); }
  };

  const revokeInvite = async (id) => {
    try {
      const { data } = await api.delete(`/orgs/${kind}/${orgId}/team/invitations/${id}`);
      setState((s) => ({ ...s, invitations: data.invitations }));
      toast.success("Invitation revoked.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revoke the invitation.");
    }
  };

  const changeRole = async (memberId, newRole) => {
    try {
      // Analytics Portal V1 §9.4: demoting someone to Contributor must never silently hand
      // them analytics access, so the demotion explicitly clears the permission.
      const body = { role: newRole };
      if (newRole === "contributor") body.permissions = { view_analytics: false };
      const { data } = await api.patch(`/orgs/${kind}/${orgId}/team/${memberId}`, body);
      apply(data);
      toast.success("Role updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update role.");
    }
  };

  // Analytics Portal V1 §9.4 — the ONLY Contributor analytics permission control. Owners and
  // Administrators may change it; everyone else sees it read-only. Absent data means OFF.
  const setViewAnalytics = async (memberId, on) => {
    setSavingPermission(memberId);
    try {
      const { data } = await api.patch(`/orgs/${kind}/${orgId}/team/${memberId}`, {
        role: "contributor", permissions: { view_analytics: on },
      });
      apply(data);
      toast.success(on ? "Analytics access granted." : "Analytics access removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update analytics access.");
    } finally { setSavingPermission(null); }
  };

  const removeMember = async () => {
    if (!removeTarget) return;
    try {
      const { data } = await api.delete(`/orgs/${kind}/${orgId}/team/${removeTarget.user_id}`);
      apply(data);
      toast.success("Member removed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove member.");
    } finally { setRemoveTarget(null); }
  };

  const transfer = async () => {
    if (!transferTo) return;
    try {
      const { data } = await api.post(`/orgs/${kind}/${orgId}/transfer`, { user_id: transferTo });
      apply(data);
      setConfirmTransfer(false);
      setTransferTo("");
      toast.success("Ownership transferred.");
      reload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not transfer ownership.");
    }
  };

  if (!state) return null;
  const members = state.members || [];
  const invitations = state.invitations || [];
  const transferable = members.filter((m) => m.role !== "owner");
  const canManageRoles = ["owner", "admin"].includes(state.my_role);
  const transferName = members.find((m) => m.user_id === transferTo)?.display_name || "This member";

  return (
    <Card className="p-6" data-testid="org-team-roles">
      <h2 className="text-lg font-[640]">Team &amp; Roles</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Grant teammates permission to post and manage this {kind === "capital" ? "capital profile" : "business"}.
        <span className="block">
          <span className="font-[560] text-foreground">Admins</span> manage content &amp; the team ·{" "}
          <span className="font-[560] text-foreground">Contributors</span> post and manage their own updates.
        </span>
      </p>

      <div className="mt-4 divide-y divide-border" data-testid="org-team-list">
        {members.map((m) => (
          <div key={m.user_id} className="py-3" data-testid={`team-member-${m.user_id}`}>
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={m.headshot_url || undefined} alt={m.display_name} />
              <AvatarFallback className="text-xs">{initials(m.display_name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-[560]">{m.display_name}</div>
              {m.handle && <div className="truncate text-xs text-muted-foreground">@{m.handle}</div>}
            </div>
            {m.role === "owner" ? (
              <StatusBadge tone={ROLE_TONE.owner} data-testid={`team-role-badge-${m.user_id}`}>Owner</StatusBadge>
            ) : (
              <>
                <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                  <SelectTrigger className="h-9 w-36" data-testid={`team-role-select-${m.user_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="contributor">Contributor</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive"
                        onClick={() => setRemoveTarget(m)} data-testid={`team-remove-${m.user_id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          {m.role === "contributor" && (
            <div className="mt-2 flex flex-wrap items-center gap-2 sm:pl-12"
                 data-testid={`team-analytics-permission-${m.user_id}`}>
              <Switch
                id={`view-analytics-${m.user_id}`}
                checked={Boolean(m.permissions?.view_analytics)}
                disabled={!canManageRoles || savingPermission === m.user_id}
                onCheckedChange={(value) => setViewAnalytics(m.user_id, value)}
                data-testid={`team-view-analytics-${m.user_id}`}
              />
              <Label htmlFor={`view-analytics-${m.user_id}`}
                     className="cursor-pointer text-xs text-muted-foreground">
                View Analytics
                <span className="ml-1 text-muted-foreground/80">
                  {canManageRoles
                    ? "— let this contributor open analytics for this profile"
                    : "— only owners and admins can change this"}
                </span>
              </Label>
            </div>
          )}
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-lg border border-border p-4">
        <Label className="text-sm font-[560]">Add a teammate</Label>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <Input
            type="email"
            placeholder="member@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMember(); } }}
            className="flex-1"
            data-testid="team-add-email"
          />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="sm:w-40" data-testid="team-add-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="contributor">Contributor</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={addMember} disabled={adding || !email.trim()} data-testid="team-add-button">
            {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Add
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          We&apos;ll send an invitation if they can join. They&apos;ll appear on the team only after they accept.
        </p>
      </div>

      {invitations.length > 0 && (
        <div className="mt-4 rounded-lg border border-border p-4" data-testid="org-pending-invites">
          <Label className="text-sm font-[560]">Pending invitations</Label>
          <p className="mt-1 text-xs text-muted-foreground">Awaiting the invitee&apos;s response.</p>
          <div className="mt-3 divide-y divide-border">
            {invitations.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 py-2.5" data-testid={`team-invite-${inv.id}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-[560]">{inv.email}</div>
                  <div className="truncate text-xs text-muted-foreground">Invited {fmtDate(inv.created_at)}</div>
                </div>
                <StatusBadge tone={ROLE_TONE[inv.role] || "neutral"}>{inv.role === "admin" ? "Admin" : "Contributor"}</StatusBadge>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive"
                        onClick={() => revokeInvite(inv.id)} data-testid={`team-invite-revoke-${inv.id}`}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isOwner && transferable.length > 0 && (
        <div className="mt-4 rounded-lg border border-border p-4" data-testid="org-transfer-card">
          <Label className="text-sm font-[560]">Transfer ownership</Label>
          <p className="mt-1 text-xs text-muted-foreground">
            Hand this profile to another member. You&apos;ll be demoted to <span className="font-[560] text-foreground">admin</span>.
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <Select value={transferTo} onValueChange={setTransferTo}>
              <SelectTrigger className="flex-1" data-testid="transfer-select">
                <SelectValue placeholder="Choose a member" />
              </SelectTrigger>
              <SelectContent>
                {transferable.map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>
                    {m.display_name}{m.handle ? ` (@${m.handle})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="secondary" disabled={!transferTo} onClick={() => setConfirmTransfer(true)} data-testid="transfer-button">
              <Crown className="mr-2 h-4 w-4" /> Transfer
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="team-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They&apos;ll lose access to post and manage this profile. Their existing posts remain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeMember} data-testid="team-remove-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmTransfer} onOpenChange={(o) => !o && setConfirmTransfer(false)}>
        <AlertDialogContent data-testid="transfer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer ownership to {transferName}?</AlertDialogTitle>
            <AlertDialogDescription>
              They&apos;ll become the owner and can publish, change the URL/@handle, and delete this profile.
              You&apos;ll be demoted to admin. This can only be reversed by the new owner.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={transfer} data-testid="transfer-confirm">Transfer ownership</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
